import type { ICellRendererParams, ValueFormatterParams, ValueGetterParams } from 'ag-grid-community';
import { get } from 'lodash-es';
import type { ReactNode } from 'react';
import { Order } from '../../../types/Order';
import { prettyWarningSeverity } from '../../../types/WarningSeverity';
import type { AgGridWarningProps } from '../../AgGrid/types';
import { OrderWarnings } from '../../OrderWarnings';
import { BlotterAggFunc } from '../aggFuncs';
import { baseColumn } from './baseColumn';
import type { ColDefFactory, Column } from './types';
import { WARNING_COLID, type WarningColumnValue } from './warning.types';

export interface WarningColumnParams {
  tooltipWidth?: string;
  /**
   * Static tooltip used when the cell has a warning severity and the row is not an `Order`.
   * `Order` rows render `OrderWarnings` from `data.warning` instead.
   */
  tooltip?: ReactNode;
}

const WARNING_COL_WIDTH = 32;
export const warning: ColDefFactory<Column<WarningColumnParams>> = column => ({
  ...baseColumn(column),
  colId: column.id ?? WARNING_COLID,
  initialWidth: WARNING_COL_WIDTH,
  maxWidth: WARNING_COL_WIDTH,
  minWidth: WARNING_COL_WIDTH,
  cellClass: 'ag-cell-warning',
  headerClass: 'ag-header-no-title',
  headerComponent: 'warningHeader',
  resizable: false,
  // If null is provided, render warning cell not pinned to the left.
  pinned: column.pinned === null ? column.pinned : 'left',
  cellRenderer: 'warningColumn',
  sortable: true,
  sortingOrder: [null, 'desc', 'asc'],
  suppressHeaderMenuButton: true,
  suppressHeaderContextMenu: true,
  cellRendererParams: (params: ICellRendererParams<unknown, WarningColumnValue | undefined>): AgGridWarningProps => {
    if (params.data instanceof Order && params.data.warning) {
      return {
        ...params,
        tooltipWidth: column.params?.tooltipWidth,
        tooltip: <OrderWarnings warning={params.data.warning} />,
      };
    }

    // The base case where we just have a severity and no data specific data type
    if (params.value?.severity != null) {
      return {
        ...params,
        tooltipWidth: column.params?.tooltipWidth,
        tooltip: column.params?.tooltip,
      };
    }

    // If there exists no severity on the value, explicitly set value to completely undefined
    return {
      ...params,
      value: undefined,
    };
  },
  initialAggFunc: column.initialAggFunc ?? (column.aggregate ? BlotterAggFunc.Warning : undefined),
  valueFormatter: (params: ValueFormatterParams<unknown, WarningColumnValue | undefined>) => {
    return params.value == null ? '' : prettyWarningSeverity(params.value.severity, params.context.current.intl);
  },
  /**
   * The point of this value getter is just to do normal value getting (based off some provided field), but also attach
   * the node id to the value. (This is typed into the WarningColumnValue). This is done such that when warnings are aggregated
   * (think "bubbled up to the top"), a higher-level cell renderer can be much more flexible about what to render given the knowledge
   * of what node's warning has been aggregated to the top.
   */
  valueGetter: (params: ValueGetterParams<unknown>): WarningColumnValue | undefined => {
    const data = params.node?.data;
    if (!data || !column.field) {
      return undefined;
    }

    return {
      ...get(data, column.field),
      nodeId: params.node?.id,
    };
  },
  comparator: (a: WarningColumnValue | undefined, b: WarningColumnValue | undefined) => {
    return (a?.severity || 0) - (b?.severity || 0);
  },
});
